import React, { ReactNode, useRef, useState } from "react";
import type * as controls from "../PhoenixFormsV4.Controls";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import PNXLabel from "./PNXLabel";
import { PNXTag } from "./PhoenixPhotoTagGroup";


const datePickerToISO = (str: string) => {
  if (!str) return "";

  const sstr = str.split("/");

  let month = sstr[0];
  let day = sstr[1];
  const year = sstr[2];

  if (month.length === 1) month = "0" + month;
  if (day.length === 1) day = "0" + day;

  return `${year}-${month}-${day}`;
};

const dateISOToPicker = (str: string) => {
  if (!str) return null;

  const sstr = str.split("-");

  return `${sstr[1]}/${sstr[2]}/${sstr[0]}`;
};

export const timeISOToPicker = (str: string): Date => {
  if (!str) return null;

  let hour = 0;
  let minute = 0;

  const timeAmPmSplit = str.split(" ");
  if (timeAmPmSplit[1] === "PM")
    hour += 12;

  const hourMinuteSplit = timeAmPmSplit[0].split(":");
  hour += parseInt(hourMinuteSplit[0]);
  minute += parseInt(hourMinuteSplit[1]);

  const pickerDate = new Date();
  pickerDate.setHours(hour, minute, 0, 0);
  return pickerDate;
};

// Convert "02:30:00 PM" format to "HH:mm" (24-hour) for MTimePicker
export const timeISOToMTimePicker = (str: string): string | null => {
  if (!str) return null;

  let hour = 0;
  let minute = 0;

  const timeAmPmSplit = str.split(" ");
  const isPM = timeAmPmSplit[1] === "PM";

  const hourMinuteSplit = timeAmPmSplit[0].split(":");
  hour = parseInt(hourMinuteSplit[0]);
  minute = parseInt(hourMinuteSplit[1]);

  // Convert 12-hour to 24-hour
  if (isPM && hour !== 12) hour += 12;
  if (!isPM && hour === 12) hour = 0;

  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
};

// Convert "HH:mm" to "HH:mm:00" for storage
const mTimePickerToISO = (timeString: string): string => {
  if (!timeString) return "";
  return `${timeString}:00`;
};

const timePickerToISO = (date: Date) => {
  if (!date) return "";

  let hour = `${date.getHours()}`;
  let minute = `${date.getMinutes()}`;

  if (hour.length === 1) hour = "0" + hour;
  if (minute.length === 1) minute = "0" + minute;

  return `${hour}:${minute}:00`;
};

interface Props {
  controlProps: controls.FormControl;
  updateValue: (newValue: string) => void;
  onControlBlur: () => void;
};

const PhoenixInput = ({
  controlProps,
  updateValue,
  onControlBlur,
} : Props) => {

  const id = `phoenixinput-${controlProps.id}`;

  // integer, decimal, and default text need to only trigger when value has changed and control loses focus (i.e. same as native html onChange event)
  const [currentValue, setCurrentValue] = useState<string>(controlProps.value ?? "");
  const lastValue = useRef<string>(controlProps.value ?? "");
  const blurUpdate = () => {
    if (lastValue.current !== currentValue) {
      lastValue.current = currentValue;
      updateValue(currentValue);
      onControlBlur();
    }
  }

  const controlledUpdate = (newValue: string) => {
    switch (controlProps.dataType) {
      case "integer":
        if (!/^-?[0-9]*$/g.test(newValue)) {
          setCurrentValue(newValue);
          return;
        }
        break;
      case "decimal":
        if (!/^-?([0-9]*)(\.)?([0-9]+)?$/g.test(newValue)){
          setCurrentValue(newValue);
          return;
        }
        break;
    }

    setCurrentValue(newValue);
  };  

  let inputField: ReactNode = null;

  switch (controlProps.dataType) {
    case "integer":
    case "decimal":
      inputField = (
        <TextField
          id={id}
          variant="standard"
          onChange={(e) => { controlledUpdate(`${e.target.value}`); }}
          onBlur={blurUpdate}
          value={currentValue}
          //type="number" // default browser event behavior of actual number fields is kind of terrible. If we need numeric keyboard on mobile I suggest modifying the input with a ref and https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/inputmode
          placeholder="Enter Number..."
        />
      );
      break;
    case "date": // xml: yyyy-mm-dd  display: mm/dd/yyyy
      // these ranges were put in for SD-25563 - the issue is that for date of birth fields, you had to open the date picker twice in order to go back more than 50 years. this allows us to select a wider range without reopening the picker
      // if these years are too large or too small, feel free to adjust, they were just random guidelines picked by jordan and dubie
      const minDate = new Date();
      minDate.setFullYear(minDate.getFullYear() - 100);
      const maxDate = new Date();
      maxDate.setFullYear(maxDate.getFullYear() + 50);

      inputField = (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            value={dateISOToPicker(controlProps.value) ? new Date(dateISOToPicker(controlProps.value)) : null}
            onChange={(dateObject) => { if (dateObject) { updateValue(datePickerToISO(`${dateObject.getMonth() + 1}/${dateObject.getDate()}/${dateObject.getFullYear()}`)); } }}
            minDate={minDate}
            maxDate={maxDate}
            slotProps={{ textField: { id, variant: "standard", onBlur: () => { onControlBlur(); } } }}
          />
        </LocalizationProvider>
      );
      break;
    case "time": // xml: hh:mm:ss    display: hh:mm am/pm
      inputField = (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <TimePicker
            value={timeISOToPicker(controlProps.value)}
            onChange={(dateObject) => { if (dateObject) { updateValue(timePickerToISO(dateObject)); } }}
            slotProps={{ textField: { id, variant: "standard", onBlur: () => { onControlBlur(); } } }}
          />
        </LocalizationProvider>
      );
      break;
    default:
      inputField = (
        <TextField
          id={id}
          variant="standard"
          onChange={(e) => { controlledUpdate(`${e.target.value}`); }}
          onBlur={blurUpdate}
          value={currentValue}
          placeholder="Enter Value..."
        />
      );
      break;
  }

  
  
  return (
    <div className={`data-type-${controlProps.dataType || 'string'}`}>
      <PNXLabel controlType="label" htmlFor={id} helpTopicId={controlProps.topicId}>{controlProps.label}</PNXLabel>
      <div className="phoenixinput-and-tag">
        {inputField}
        {controlProps.hint && <PNXTag tag={controlProps.hint} />}
      </div>
    </div>
  );
};

export default PhoenixInput;